import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'actionMessage'
	| 'pageSelectorLabel'
	| 'replyingAsLabel'
	| 'replyWithAgentText'
	| 'commentMessageLabel'
	| 'commentPlaceholder'
	| 'publishButton'
	| 'confluenceCommentCreateActionCardTitle'
	| 'confluenceCommentCreateInlineActionCardTitle'
	| 'replyButton'
	| 'confluenceCommentCreateReplyActionCardTitle';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		actionMessage: {
			id: 'ai-mate.message-actions.confluence-comment-create.action-message',
			defaultMessage:
				"Here's the comment I suggest adding to the page {name}. Take a look over it, then publish when you're ready.",
			description:
				'Message text displayed in an action card within the AI conversation interface when the AI suggests adding a comment to a Confluence page. Shown as the main message text in the action card body. The name placeholder will be substituted with the target Confluence page name (e.g., "Project Planning"). The <bold> tags will be rendered as bold text for "Publish". The purpose is to present the AI-generated comment content to the user for review and provide clear instructions on how to proceed with publishing the comment.',
		},
		pageSelectorLabel: {
			id: 'ai-mate.message-actions.confluence-comment-create.page-selector-label',
			defaultMessage: 'Page',
			description:
				'Label text displayed above or next to a page selector dropdown in the Confluence comment creation action card within the AI conversation interface. Shown as a form field label. The purpose is to identify the page selector control, which allows users to choose which Confluence page the comment will be added to.',
		},
		replyingAsLabel: {
			id: 'ai-mate.message-actions.confluence-comment-create.replying-as-label',
			defaultMessage: 'Replying as',
			description:
				'Label text displayed above the replying with Agent info section in the Confluence comment creation action card within the AI conversation interface. Shown as a form field label.',
		},
		replyWithAgentText: {
			id: 'ai-mate.message-actions.confluence-comment-create.reply-with-agent',
			defaultMessage: '{userName} with you',
			description:
				'Text displayed in the replying as section of the Confluence comment creation action card within the AI conversation interface. Shown beside overlapping avatars to indicate the agent/person replying together with the current user.',
		},
		commentMessageLabel: {
			id: 'ai-mate.message-actions.confluence-comment-create.comment-message-label',
			defaultMessage: 'Message',
			description:
				'Label text displayed above the comment text box in the Confluence comment creation action card within the AI conversation interface. Shown as a form field label.',
		},
		commentPlaceholder: {
			id: 'ai-mate.message-actions.confluence-comment-create.page-content-placeholder',
			defaultMessage: 'Write your comment here',
			description:
				'Placeholder text displayed in a text input field within the Confluence comment creation action card in the AI conversation interface. Shown as placeholder text when the input field is empty. The purpose is to guide users on what they should enter in the comment input field, prompting them to write or edit the comment content that will be added to the Confluence page.',
		},
		publishButton: {
			id: 'ai-mate.message-actions.confluence-comment-create.publish-button',
			defaultMessage: 'Publish',
			description:
				'Button label displayed on the primary action button in the Confluence comment creation action card within the AI conversation interface. Shown as the text on a button at the bottom of the action card. When clicked, it publishes the comment to the selected Confluence page. The purpose is to provide a clear call-to-action for users to confirm and execute the comment creation.',
		},
		confluenceCommentCreateActionCardTitle: {
			id: 'ai-mate.message-actions.confluence-comment-create.action-card-title',
			defaultMessage: 'Add comment',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests adding a comment to a Confluence page. Shown as the card header title. The purpose is to immediately communicate to the user what action the card represents, which is adding a comment to a Confluence page.',
		},
		confluenceCommentCreateInlineActionCardTitle: {
			id: 'ai-mate.message-actions.confluence-comment-create.action-card-title-inline',
			defaultMessage: 'Add inline comment',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests adding a comment to a Confluence page. Shown as the card header title. The purpose is to immediately communicate to the user what action the card represents, which is adding an inline comment to a Confluence page.',
		},
		replyButton: {
			id: 'ai-mate.message-actions.confluence-comment-create.reply-button',
			defaultMessage: 'Add reply',
			description:
				'Button label displayed on the reply button in the Confluence comment creation action card within the AI conversation interface. Shown as the text on a button at the bottom of the action card. When clicked, it replies to the comment. The purpose is to provide a clear call-to-action for users to confirm and execute the reply.',
		},
		confluenceCommentCreateReplyActionCardTitle: {
			id: 'ai-mate.message-actions.confluence-comment-create.action-card-title-reply',
			defaultMessage: 'Reply in thread',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests replying to a Confluence page comment. Shown as the card header title. The purpose is to immediately communicate to the user what action the card represents, which is replying to a Confluence page comment.',
		},
	});

export default message;
